import { useEffect, useState } from "react";
import Snackbar from "@mui/material/Snackbar";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import CloseIcon from "@mui/icons-material/Close";
import logo from "@/assets/jobyoda-logo.webp";

type BeforeInstallPromptEvent = Event & {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
};

const DISMISS_KEY = "jobyoda-install-dismissed-at";
const DISMISS_TTL_DAYS = 14;

export function InstallAppPrompt() {
  const [deferred, setDeferred] = useState<BeforeInstallPromptEvent | null>(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") return;

    // Register the minimal service worker — required for installability on Chrome/Edge.
    if ("serviceWorker" in navigator) {
      navigator.serviceWorker.register("/sw.js").catch(() => {
        // ignore — install prompt is optional UX
      });
    }

    const dismissedAt = Number(window.localStorage.getItem(DISMISS_KEY) ?? 0);
    const recentlyDismissed =
      dismissedAt && Date.now() - dismissedAt < DISMISS_TTL_DAYS * 24 * 60 * 60 * 1000;

    const handler = (e: Event) => {
      e.preventDefault();
      if (recentlyDismissed) return;
      setDeferred(e as BeforeInstallPromptEvent);
      setOpen(true);
    };

    window.addEventListener("beforeinstallprompt", handler);
    return () => window.removeEventListener("beforeinstallprompt", handler);
  }, []);

  const handleInstall = async () => {
    if (!deferred) return;
    await deferred.prompt();
    await deferred.userChoice;
    setDeferred(null);
    setOpen(false);
  };

  const handleDismiss = () => {
    if (typeof window !== "undefined") {
      window.localStorage.setItem(DISMISS_KEY, String(Date.now()));
    }
    setOpen(false);
  };

  if (!open) return null;

  return (
    <Snackbar
      open={open}
      anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      sx={{ mb: { xs: 2, sm: 3 } }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 2,
          px: 2,
          py: 1.5,
          borderRadius: 3,
          bgcolor: "background.paper",
          border: "1px solid",
          borderColor: "divider",
          boxShadow: "0 12px 40px rgba(0,0,0,0.25)",
          maxWidth: 460,
        }}
      >
        <Box
          component="img"
          src={logo}
          alt="JobYoda"
          sx={{ width: 44, height: 44, flexShrink: 0 }}
        />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
            Install JobYoda
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Add to your home screen for one-tap access.
          </Typography>
        </Box>
        <Button onClick={handleInstall} variant="contained" size="small">
          Install
        </Button>
        <IconButton onClick={handleDismiss} size="small" aria-label="Dismiss install prompt">
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>
    </Snackbar>
  );
}
